import h from 'hyperscript';

import smallWarningIcon from '@disy/cadenza-icons/warning.svg';
import { icon } from '@disy/ui/icon';

import { Modal } from 'ui/dialog/modal/modal';

import { Command } from 'cadenza/api-client/workbook-commands';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeHighlightingRule } from 'cadenza/workbook/attribute-modification-dialog/presentation/attribute-highlighting/attribute-highlighting-types';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';

import './remove-attribute-dialog-check-highlighting-rules.css';
import i18n from './remove-attribute-dialog-check-highlighting-rules.properties';

/**
 * Checks whether removing the given attribute would break attribute-highlighting rules on other attributes.
 *
 * Scans all provided attributes for highlighting rules that reference the `attributeId`.
 * Returns:
 *  - the names of dependent rules (for confirmation UI), and
 *  - commands that update affected attributes to remove those rules (and disable table-cell presentation
 *    if no highlighting rules remain on an attribute).
 *
 * Intended to be used before executing an attribute removal command, so the caller can prompt the user and,
 * on confirmation, execute the returned commands together with the actual remove command.
 *
 * @param attributeId - Attribute id that is going to be removed.
 * @param allAttributes - All attributes currently present in the data view.
 * @param dataViewId - The data view in which the rules should be checked/updated.
 * @returns An object of `{dependantHighlightingRuleNames, commandsToResetRules}`.
 */
export function checkHighlightingRules(
  attributeId: ActiveAttributeId,
  allAttributes: WorkbookAttributeDto[],
  dataViewId: DataViewId,
) {
  const allRelatedRuleNames: string[] = [];
  const commandsToResetRules: Command[] = [];

  allAttributes.forEach((attr) => {
    // No need to check the rules assigned to the attribute itself.
    if (attr.id === attributeId) {
      return;
    }

    const relatedRuleNames = new Set<string>();
    const rulesToKeep: AttributeHighlightingRule[] = [];
    attr.attributeHighlightingRules?.forEach((rule) => {
      let ruleHasRelatedCondition = false;
      rule.valueRules.forEach(({ activeAttributeId }) => {
        if (activeAttributeId === attributeId) {
          relatedRuleNames.add(rule.name);
          ruleHasRelatedCondition = true;
        }
      });
      // Since rule names are not unique, we have to use a different check.
      if (!ruleHasRelatedCondition) {
        rulesToKeep.push(rule);
      }
    });
    // If length of "rulesToKeep" is equal to length of "attributeHighlightingRules", there was no
    // relation found. No need to send a command if that's the case.
    if (attr.attributeHighlightingRules?.length && rulesToKeep.length !== attr.attributeHighlightingRules.length) {
      commandsToResetRules.push(
        Command.SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES({
          dataViewId,
          attributeId: attr.id as ActiveAttributeId,
          attributeHighlightingRules: rulesToKeep,
        }),
      );
      allRelatedRuleNames.push(...relatedRuleNames);

      // There are no rules to keep in this case, meaning all rules should be removed. We also need
      // to remove the highlighting presentation setting altogether since it's not allowed to have
      // it active without having at least one rule defined.
      if (rulesToKeep.length === 0) {
        commandsToResetRules.push(
          Command.REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION({
            dataViewId,
            attributeId: attr.id as ActiveAttributeId,
            modificationType: AttributeModificationKey.TABLE_CELL_PRESENTATION,
          }),
        );
      }
    }
  });

  return { dependantHighlightingRuleNames: allRelatedRuleNames, commandsToResetRules };
}

/**
 * Checks whether removing the given attributes would break attribute-highlighting rules on other attributes.
 *
 * @param attributeIds - Attribute ids that are going to be removed.
 * @param allAttributes - All attributes currently present in the data view.
 * @param dataViewId - The data view in which the rules should be checked/updated.
 * @returns An object of `{dependantHighlightingRuleNames, commandsToResetRules}`.
 * @see checkHighlightingRules
 */
export function checkHighlightingRulesForMultipleAttributes(
  attributeIds: ActiveAttributeId[],
  allAttributes: WorkbookAttributeDto[],
  dataViewId: DataViewId,
) {
  const allDependantHighlightingRuleNames: string[] = [];
  const allCommandsToResetRules: Command[] = [];

  for (const attributeId of attributeIds) {
    const { dependantHighlightingRuleNames, commandsToResetRules } = checkHighlightingRules(
      attributeId,
      allAttributes,
      dataViewId,
    );
    allDependantHighlightingRuleNames.push(...dependantHighlightingRuleNames);
    allCommandsToResetRules.push(...commandsToResetRules);
  }

  return {
    dependantHighlightingRuleNames: allDependantHighlightingRuleNames,
    commandsToResetRules: allCommandsToResetRules,
  };
}

export function showModalToConfirmAttributeRemoval(
  dependantHighlightingRuleNames: string[],
  submitCallback: () => void,
) {
  new ConfirmAttributeRemovalModal(dependantHighlightingRuleNames, submitCallback).connectAndShow();
}

class ConfirmAttributeRemovalModal extends Modal {
  readonly #submitCallback;
  constructor(dependantHighlightingRuleNames: string[], submitCallback: () => void) {
    super({
      titleIcon: icon(smallWarningIcon),
      title: i18n('highlighting.remove.attribute.modal.header'),
      content: () =>
        Promise.resolve(
          h('section', [
            h('h2', i18n('highlighting.remove.attribute.modal.heading')),
            h('p', i18n('highlighting.remove.attribute.modal.text', { rulesNames: dependantHighlightingRuleNames })),
          ]),
        ),
      submitButton: i18n('highlighting.remove.attribute.modal.confirmDelete'),
      cancelButton: true,
    });
    this.#submitCallback = submitCallback;
    this.dialog.classList.add('attribute-remove-dialog-check-highlighting-rules');
  }

  override onSubmit() {
    this.#submitCallback();
  }
}
